import { type GetFolderTicketsPaginatedResponse } from "@mg/schemas/src/christo/catalyst";
import { Folder, Robot } from "@phosphor-icons/react";
import {
  Avatar,
  Badge,
  Flex,
  Grid,
  Heading,
  HoverCard,
  IconButton,
  Skeleton,
  Table,
  Text,
} from "@radix-ui/themes";
import { useRouter } from "@tanstack/react-router";
import cx from "classnames";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { validate } from "uuid";

import { TicketActionsDropdown } from "./TicketActionsDropdown";
import { renderAIPersonaAvatar } from "./TicketGrid";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import { UploadProgress } from "../../../components/UploadProgress";
import { useAnalytics } from "../../../utils/analytics";
import { isPunttGuest } from "../../../utils/auth";
import { isNil } from "../../../utils/fp";
import { useAppSelector } from "../../../utils/hooks";
import { ticketsRoute } from "../route";
import { ticketRoute } from "../routes/ticket/route";
import { DragDropProps } from "../view";

const MAX_PARTICIPANTS_VISIBLE = 4;

type ResponseTicket = GetFolderTicketsPaginatedResponse["tickets"][number];
type ResponseFolder = NonNullable<
  GetFolderTicketsPaginatedResponse["folders"]
>[number];
type TicketOrFolder = ResponseTicket | ResponseFolder;

function isFolder(t: TicketOrFolder): t is ResponseFolder {
  return "isFolder" in t && Boolean(t.isFolder);
}

type TicketTableProps = {
  onDeleteTicket: (ticketId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onTicketClick: (ticketId: string) => void;
  onFolderClick: (folderId: string) => void;
  onRenameFolder: (folderId: string) => void;
  onShareTicket: (ticketId: string) => void;
  onShareFolder: (folderId: string) => void;
  onDownloadFolder: (folderId: string) => void;
  onDownloadTicket: (ticketId: string) => void;
  onManageTicketVersions: (ticketId: string) => void;
  uploadState?: any;
  onCancelUpload?: () => void;
} & DragDropProps;

export function TicketTable(props: TicketTableProps) {
  const { t: translate } = useTranslation("ui");
  const tickets = useAppSelector((state) => state.punttProjects.tickets);
  const folders = useAppSelector((state) => state.punttProjects.folders);
  const posthog = useAnalytics();
  const router = useRouter();
  const markedFolders = useMemo(
    () =>
      folders.map((f) => ({
        ...f,
        isFolder: true,
      })),
    [folders],
  );
  const mergedFoldersAndTickets: TicketOrFolder[] = useMemo(
    () =>
      [...markedFolders, ...tickets].toSorted((a, b) => {
        const dateA = new Date(a.updatedAt as string);
        const dateB = new Date(b.updatedAt as string);

        // Sort in descending order (most recent first)
        return dateB.getTime() - dateA.getTime();
      }),
    [markedFolders, tickets],
  );

  const {
    onDeleteTicket,
    onDeleteFolder,
    onRenameFolder,
    onShareTicket,
    onShareFolder,
    onDownloadFolder,
    onDownloadTicket,
    onManageTicketVersions,
  } = props;

  return (
    <Table.Root
      variant="surface"
      className="mx-10"
      onDragEnter={(e) => {
        if (props.isDraggingCards) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onDragOver={(e) => {
        if (props.isDraggingCards) {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
        }
      }}
      onDrop={(e) => {
        if (props.isDraggingCards) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>
            {translate("common:labels.name")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>
            {translate("tickets.ticketTable.lastUpdated")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell align="right">
            {translate("tickets.ticketTable.participants")}
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell align="center" style={{ width: 44 }}>
            {translate("tickets.ticketTable.ai")}
          </Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {mergedFoldersAndTickets.map((t, i) => {
          // t is uploading
          if (isNil(t._id) || validate(t._id)) {
            if (isFolder(t)) {
              return (
                <Table.Row key={`.${i}.${t.name}`}>
                  <Table.Cell width="100%">
                    <Flex align="center" gap="3">
                      <Folder />
                      <Heading size="2" className="truncate">
                        {t.name}
                      </Heading>
                      {props.uploadState && props.uploadState.isUploading && (
                        <UploadProgress
                          isUploading={props.uploadState.isUploading}
                          progress={props.uploadState.progress}
                          fileName={props.uploadState.fileName}
                          error={props.uploadState.error}
                          isComplete={props.uploadState.isComplete}
                          onCancel={props.onCancelUpload}
                          variant="minimal"
                        />
                      )}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell className="min-w-48 whitespace-nowrap">
                    <Flex gap="2" justify="end">
                      <Skeleton>
                        <Avatar fallback="" size="1" />
                      </Skeleton>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              );
            }

            return (
              <Table.Row key={`.${i}.${t.title}`}>
                <Table.Cell width="100%" className="align-middle">
                  <Heading size="2" className="truncate">
                    <button
                      type="button"
                      data-testid={`ticket-row-uploading-${t._id}`}
                      className="m-0 w-full cursor-pointer truncate border-none bg-transparent p-0 text-left"
                      tabIndex={0}
                      aria-label={`Uploading ticket ${t.title}`}
                      disabled
                    >
                      {t.title}
                    </button>
                  </Heading>
                </Table.Cell>
                <Table.Cell className="min-w-48 whitespace-nowrap align-middle">
                  <UploadProgress
                    isUploading={props.uploadState?.isUploading}
                    progress={props.uploadState?.progress}
                    fileName={props.uploadState?.fileName}
                    error={props.uploadState?.error}
                    isComplete={props.uploadState?.isComplete}
                    onCancel={props.onCancelUpload}
                    variant="minimal"
                  />
                </Table.Cell>
                <Table.Cell className="min-w-48 whitespace-nowrap">
                  {/* Placeholder for participants */}
                  <Flex gap="1" justify="end">
                    <Skeleton>
                      <Avatar fallback="" size="1" />
                    </Skeleton>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            );
          }

          // t is uploaded
          return (
            <Table.Row
              key={t._id}
              data-testid={`ticket-row-${t._id}`}
              draggable={true}
              onClick={(e) => props.onSelect(t._id, e)}
              onDoubleClick={() => {
                posthog.capture("double_clicked_tickets_card", {
                  id: t._id,
                  is_folder: isFolder(t),
                  name: isFolder(t) ? t.name : t.title,
                });
                if (isFolder(t)) {
                  props.onFolderClick(t._id);
                } else {
                  props.onTicketClick(t._id);
                }
              }}
              onDragStart={(e) => props.onDragStart(t._id, e)}
              onDragEnter={(e) => props.onDragEnter(t._id, e)}
              onDragLeave={(e) => props.onDragLeave(t._id, e)}
              onDragOver={props.onDragOver}
              onDrop={(e) => props.onDrop(t._id, e)}
              className={cx(
                "group cursor-pointer transition-colors hover:bg-puntt-blue-2 active:bg-puntt-blue-3",
                {
                  "bg-puntt-blue-2":
                    props.dropTargetId === t._id ||
                    props.selectedIds.includes(t._id),
                  "opacity-50":
                    props.isDraggingCards && props.selectedIds.includes(t._id),
                  hidden: props.isRemoving.has(t._id),
                },
              )}
            >
              <Table.Cell width="100%" className="align-middle">
                <Grid gap="3" columns="auto auto auto auto" align="center">
                  {isFolder(t) && <Folder />}
                  <Heading size="2" className="truncate">
                    {isFolder(t) ? (
                      <button
                        type="button"
                        data-testid={`ticket-row-folder-${t._id}`}
                        className="m-0 w-full cursor-pointer truncate border-none bg-transparent p-0 text-left hover:underline focus:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onFolderClick(t._id);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const url = router.buildLocation({
                            to: ticketsRoute.to,
                            search: { folderId: t._id },
                          });
                          window.open(url.href, "_blank");
                        }}
                        tabIndex={0}
                        aria-label={`Open folder ${t.name}`}
                      >
                        {t.name}
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid={`ticket-name-${t._id}`}
                        className="m-0 w-full cursor-pointer truncate border-none bg-transparent p-0 text-left hover:underline focus:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onTicketClick(t._id);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const url = router.buildLocation({
                            to: ticketRoute.to,
                            params: { ticketId: t._id },
                          });
                          window.open(url.href, "_blank");
                        }}
                        tabIndex={0}
                        aria-label={`Open ticket ${t.title}`}
                      >
                        {t.title}
                      </button>
                    )}
                  </Heading>

                  <Text
                    size="2"
                    className={cx({
                      hidden:
                        !isFolder(t) &&
                        (!(t.documentImportType ?? "").trim() ||
                          (t.documentImportType ?? "").length > 12),
                    })}
                  >
                    {!isFolder(t) &&
                      ((t.documentImportType ?? "").length <= 12
                        ? t.documentImportType
                        : t.title.includes(".")
                          ? t.title
                              .slice(t.title.lastIndexOf(".") + 1)
                              .toLocaleUpperCase()
                          : "")}
                  </Text>

                  <Badge
                    size="1"
                    color="gray"
                    variant="solid"
                    className={cx({
                      hidden:
                        ("totalRevisions" in t ? t.totalRevisions : 1) < 2,
                    })}
                  >
                    V{"totalRevisions" in t ? t.totalRevisions : 1}
                  </Badge>

                  <TicketActionsDropdown
                    className={cx(
                      "opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100",
                      {
                        hidden: isPunttGuest(),
                      },
                    )}
                    onRename={() => onRenameFolder(t._id)}
                    onShare={() =>
                      isFolder(t) ? onShareFolder(t._id) : onShareTicket(t._id)
                    }
                    onDeleteTicket={() =>
                      isFolder(t)
                        ? onDeleteFolder(t._id)
                        : onDeleteTicket(t._id)
                    }
                    onDownload={() =>
                      isFolder(t)
                        ? onDownloadFolder(t._id)
                        : onDownloadTicket(t._id)
                    }
                    {...(isFolder(t)
                      ? {}
                      : {
                          onManageVersions: () => onManageTicketVersions(t._id),
                        })}
                  />
                </Grid>
              </Table.Cell>
              <Table.Cell className="min-w-48 whitespace-nowrap align-middle">
                {t.updatedAt !== "" && !isNil(t.updatedAt)
                  ? formatDistanceToNow(new Date(t.updatedAt as string), {
                      addSuffix: true,
                    })
                  : "--"}
              </Table.Cell>
              <Table.Cell className="min-w-48 whitespace-nowrap">
                <Flex gap="1" justify="end">
                  {(
                    t.participants as (Exclude<
                      (typeof t.participants)[number],
                      string
                    > & { isAI?: boolean })[]
                  )
                    ?.slice(0, MAX_PARTICIPANTS_VISIBLE)
                    .map(({ avatar, name, _id, isAI }, i) => {
                      const showsAvatarOverflow =
                        t.participants.length > MAX_PARTICIPANTS_VISIBLE;
                      const avatarOverflowCount =
                        t.participants.length - (MAX_PARTICIPANTS_VISIBLE - 1);
                      const isLastVisibleAvatar =
                        i === MAX_PARTICIPANTS_VISIBLE - 1;

                      if (showsAvatarOverflow && isLastVisibleAvatar) {
                        return (
                          <HoverCard.Root key={`h.${t._id}.${_id}`}>
                            <HoverCard.Trigger>
                              <IconButton size="1" variant="ghost">
                                <Avatar
                                  color="red"
                                  size="1"
                                  variant="soft"
                                  fallback={`+${avatarOverflowCount}`}
                                />
                              </IconButton>
                            </HoverCard.Trigger>

                            <HoverCard.Content
                              className="dark"
                              side="top"
                              align="center"
                            >
                              <Grid gap="2">
                                {(
                                  t.participants as (Exclude<
                                    (typeof t.participants)[number],
                                    string
                                  > & { isAI?: boolean })[]
                                )
                                  .slice(MAX_PARTICIPANTS_VISIBLE - 1)
                                  .map(({ _id, avatar, name, isAI }) => (
                                    <Flex
                                      gap="3"
                                      align="center"
                                      key={`${t._id}.${_id}`}
                                    >
                                      {isAI || name === "AI Reviewer" ? (
                                        <Avatar
                                          color="amber"
                                          size="1"
                                          variant="solid"
                                          fallback={<Robot />}
                                        />
                                      ) : (
                                        <AvatarWithInitials
                                          size={6}
                                          square={true}
                                          avatar={avatar}
                                          name={name}
                                        />
                                      )}

                                      <Text size="1" className="truncate">
                                        {name}
                                      </Text>
                                    </Flex>
                                  ))}
                              </Grid>
                            </HoverCard.Content>
                          </HoverCard.Root>
                        );
                      }

                      // Prefer the server-tagged `isAI` flag (set when the
                      // user has a CATALYST_AI profile). The legacy
                      // `name === "AI Reviewer"` check is kept as a fallback
                      // for clients hitting older API responses or for
                      // enterprises whose AI user happens to be named
                      // "AI Reviewer".
                      if (isAI || name === "AI Reviewer") {
                        return (
                          <HoverCard.Root key={`h.${t._id}.${_id}`}>
                            <HoverCard.Trigger>
                              <div>
                                <Avatar
                                  data-testid="ai-avatar"
                                  color="amber"
                                  size="1"
                                  variant="solid"
                                  fallback={<Robot />}
                                />
                              </div>
                            </HoverCard.Trigger>
                            <HoverCard.Content
                              className="dark"
                              side="top"
                              align="center"
                            >
                              <Grid gap="2">
                                <Flex
                                  gap="3"
                                  align="center"
                                  key={`a.${t._id}.${_id}`}
                                >
                                  <Avatar
                                    color="amber"
                                    size="1"
                                    variant="solid"
                                    fallback={<Robot />}
                                  />
                                  <Text size="1" className="truncate">
                                    {translate(
                                      "tickets.ticketTable.aiReviewer",
                                    )}
                                  </Text>
                                </Flex>
                              </Grid>
                            </HoverCard.Content>
                          </HoverCard.Root>
                        );
                      }

                      return (
                        <HoverCard.Root key={`h.${t._id}.${_id}`}>
                          <HoverCard.Trigger>
                            <AvatarWithInitials
                              key={`a.${t._id}.${_id}`}
                              size={6}
                              square={true}
                              avatar={avatar}
                              name={name}
                            />
                          </HoverCard.Trigger>
                          <HoverCard.Content
                            className="dark"
                            side="top"
                            align="center"
                          >
                            <Grid gap="2">
                              <Flex
                                gap="3"
                                align="center"
                                key={`a.${t._id}.${_id}`}
                              >
                                <AvatarWithInitials
                                  size={6}
                                  square={true}
                                  avatar={avatar}
                                  name={name}
                                />
                                <Text size="1" className="truncate">
                                  {name}
                                </Text>
                              </Flex>
                            </Grid>
                          </HoverCard.Content>
                        </HoverCard.Root>
                      );
                    })}
                </Flex>
              </Table.Cell>
              <Table.Cell style={{ width: 44 }} align="center">
                {t.isAiReviewed && (
                  <HoverCard.Root>
                    <HoverCard.Trigger>
                      <Flex
                        data-testid="ai-review-icon"
                        align="center"
                        justify="center"
                        style={{ height: 24 }}
                      >
                        <Robot className="size-4 text-base-black" size={16} />
                      </Flex>
                    </HoverCard.Trigger>
                    <HoverCard.Content className="dark" side="top">
                      <Grid gap="2">
                        {Array.isArray(t.aiPersonas) &&
                        t.aiPersonas.length > 0 ? (
                          t.aiPersonas.map((persona) => {
                            const isKnown = ["brand", "legal", "copy"].includes(
                              persona,
                            );
                            return (
                              <Flex gap="3" align="center" key={persona}>
                                {renderAIPersonaAvatar(persona)}
                                <Text size="1">
                                  {isKnown
                                    ? `Puntt AI: ${persona.charAt(0).toUpperCase() + persona.slice(1)}`
                                    : translate("tickets.ticketTable.punttAi")}
                                </Text>
                              </Flex>
                            );
                          })
                        ) : (
                          <Flex gap="3" align="center">
                            {renderAIPersonaAvatar(undefined)}
                            <Text size="1">
                              {translate("tickets.ticketTable.punttAi")}
                            </Text>
                          </Flex>
                        )}
                      </Grid>
                    </HoverCard.Content>
                  </HoverCard.Root>
                )}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table.Root>
  );
}
